import { hsNormalizeOptionalId } from './frustum-scene-state.class';

/** A copy of `HsNodeVisibility` to restore later, for example when the server rejects a change. */
export interface HsNodeVisibilityState {
  hidden: readonly string[];
  /** Nodes left visible by an isolation, or null when nothing is isolated. */
  isolated: readonly string[] | null;
}

/**
 * Which scene nodes the viewer hides on its own, ahead of the server: the viewer applies a Hide, Unhide,
 * Isolate or Show all at once and persists it in the background. Nodes are listed one by one; callers pass
 * a subtree's node ids to hide the subtree. Node ids compare case-insensitively, like render keys.
 */
export class HsNodeVisibility {
  private hidden: Set<string> = new Set();
  private isolated: Set<string> | null = null;
  private changeCount: number = 0;

  /** Grows with every change, so callers can tell whether another change came after theirs. */
  public get revision(): number {
    return this.changeCount;
  }

  /** True when no node is hidden or isolated. */
  public get isEmpty(): boolean {
    return this.hidden.size === 0 && this.isolated === null;
  }

  public hide(nodeIds: Iterable<string>): void {
    for (const nodeId of normalize(nodeIds)) {
      this.hidden.add(nodeId);
      this.isolated?.delete(nodeId);
    }
    this.changeCount++;
  }

  /** Shows the nodes again; under an isolation they join the isolated nodes. */
  public show(nodeIds: Iterable<string>): void {
    for (const nodeId of normalize(nodeIds)) {
      this.hidden.delete(nodeId);
      this.isolated?.add(nodeId);
    }
    this.changeCount++;
  }

  /** Hides every node except `nodeIds`, which become visible. */
  public isolate(nodeIds: Iterable<string>): void {
    this.hidden = new Set();
    this.isolated = new Set(normalize(nodeIds));
    this.changeCount++;
  }

  public showAll(): void {
    this.hidden = new Set();
    this.isolated = null;
    this.changeCount++;
  }

  public isHidden(nodeId: string): boolean {
    const normalized: string = hsNormalizeOptionalId(nodeId);
    return this.hidden.has(normalized) || (this.isolated !== null && !this.isolated.has(normalized));
  }

  public snapshot(): HsNodeVisibilityState {
    return { hidden: [...this.hidden], isolated: this.isolated === null ? null : [...this.isolated] };
  }

  public restore(state: HsNodeVisibilityState): void {
    this.hidden = new Set(state.hidden);
    this.isolated = state.isolated === null ? null : new Set(state.isolated);
    this.changeCount++;
  }
}

function normalize(nodeIds: Iterable<string>): string[] {
  return [...nodeIds]
    .map((nodeId: string) => hsNormalizeOptionalId(nodeId))
    .filter((nodeId: string) => nodeId !== 'none');
}
